import { Point2d } from "../../../../common/geometry/Geom2d";
import TabletopDimensionalElement from "./TabletopDimensionalElement";
import TabletopViewport from "../TabletopViewport";

export default class TabletopRulerElement extends TabletopDimensionalElement {
    private _points: Array<Point2d>;
    private _pointToPointDistances: Array<number>;
    private _endToEndDistance: number;
    private _showDistance: boolean;
    protected _leftBound: number = 0;
    protected _rightBound: number = 0;
    protected _topBound: number = 0;
    protected _bottomBound: number = 0;

    public constructor(args: {
        points: Array<Point2d>;
        showDistance?: boolean;
    }) {
        super({ x: args.points[0]?.x ?? 0, y: args.points[0]?.y ?? 0 });

        this._points = args.points;
        this._pointToPointDistances = [];
        this._endToEndDistance = 0;
        this._showDistance =
            args.showDistance == null ? false : args.showDistance;

        this.updateBounds();
        this.updateDistances();
    }

    public clone(): TabletopRulerElement {
        return new TabletopRulerElement({ points: this._points });
    }

    public get x(): number {
        return this.points[0]?.x ?? 0;
    }

    public get y(): number {
        return this.points[0]?.y ?? 0;
    }

    public override get width(): number {
        return this._rightBound - this._leftBound;
    }

    public override get height(): number {
        return this.bottom - this.top;
    }

    public override get left(): number {
        return this._leftBound;
    }

    public override get top(): number {
        return this._topBound;
    }

    public override get right(): number {
        return this._rightBound;
    }

    public override get bottom(): number {
        return this._bottomBound;
    }

    public get points() {
        return this._points;
    }

    public get showDistance() {
        return this._showDistance;
    }
    public set showDistance(val: boolean) {
        this._showDistance = val;
    }

    /** Gets the index of the last point of this ruler. */
    public get lastPointIndex(): number {
        return this._points.length - 1;
    }

    /** Get the last point of this ruler. */
    public get lastPoint(): Point2d {
        return this._points[this._points.length - 1];
    }

    public setPointAt(index: number, newPosition: Point2d): void {
        if (index < 0 || index >= this._points.length) {
            return;
        }

        this._points[index] = newPosition;

        this.updateBounds();
        this.updateDistances();
    }

    public setLastPoint(newPosition: Point2d): void {
        this.setPointAt(this._points.length - 1, newPosition);
    }

    /** Adds a new point to the end of the point list, which will consequently update the bounds of the ruler. */
    public addPoint(point: Point2d): void {
        this._points.push(point);
        this.updateBounds();
        this.updateDistances();
    }

    public removePointAt(index: number): void {
        if (this._points.length <= 0) {
            console.error("Cannot remove the last point of a ruler element.");
            return;
        }
        if (index < 0 || index >= this._points.length) {
            return;
        }
        this._points = this._points.filter((_, idx) => idx !== index);

        this.updateBounds();
        this.updateDistances();
    }

    private updateDistances(): void {
        if (this._points.length === 1) {
            this._pointToPointDistances = [];
            this._endToEndDistance = 0;

            return;
        }

        this._pointToPointDistances = this._points
            .slice(0, this._points.length - 1)
            .map((point, idx) => {
                const point2 = this._points[idx + 1];
                const distance = Math.sqrt(
                    Math.pow(point2.x - point.x, 2) +
                        Math.pow(point2.y - point.y, 2),
                );

                return this.universe == null
                    ? distance
                    : distance / this.universe.unitLength;
            });
        this._endToEndDistance =
            Math.sqrt(
                Math.pow(
                    this._points[this._points.length - 1].x - this._points[0].x,
                    2,
                ) +
                    Math.pow(
                        this._points[this._points.length - 1].y -
                            this._points[0].y,
                        2,
                    ),
            ) / (this.universe == null ? 1 : this.universe.unitLength);
    }

    public updateBounds(): void {
        let left: number = Number.MAX_SAFE_INTEGER;
        let right: number = -Number.MAX_SAFE_INTEGER;
        let top: number = Number.MAX_SAFE_INTEGER;
        let bottom: number = -Number.MAX_SAFE_INTEGER;

        this._points.forEach((point) => {
            left = Math.min(left, point.x);
            right = Math.max(right, point.x);
            top = Math.min(top, point.y);
            bottom = Math.max(bottom, point.y);
        });

        this._leftBound = left;
        this._rightBound = right;
        this._topBound = top;
        this._bottomBound = bottom;
    }

    public getPointToPointDistance(pointIndex: number) {
        if (
            pointIndex < 0 ||
            pointIndex >= this._pointToPointDistances.length
        ) {
            return 0;
        }

        return this._pointToPointDistances[pointIndex];
    }

    public get rulerDistance(): number {
        return this._pointToPointDistances.reduce((acc, curr) => acc + curr, 0);
    }

    public get endToEndDistance(): number {
        return this._endToEndDistance;
    }

    public override draw(
        ctx: CanvasRenderingContext2D,
        viewport: TabletopViewport,
        timestamp: number,
    ) {
        if (this.points.length <= 1) {
            return;
        }

        const pointsInViewport = this.points.map((p) =>
            viewport.universePointToViewportPoint(p),
        );

        ctx.lineWidth = 3;
        ctx.strokeStyle = "black";

        ctx.beginPath();
        ctx.moveTo(pointsInViewport[0].x, pointsInViewport[0].y);

        for (let idx = 1; idx < pointsInViewport.length; ++idx) {
            ctx.lineTo(pointsInViewport[idx].x, pointsInViewport[idx].y);
        }

        ctx.stroke();

        if (this.showDistance) {
            const labelText = (
                Math.floor(this.endToEndDistance * 10) / 10
            ).toString(10);
            const point = viewport.universePointToViewportPoint(this.lastPoint);

            ctx.lineWidth = 2;
            ctx.strokeStyle = "white";
            ctx.fillStyle = "black";
            ctx.font = "bold 16px sans-serif";
            ctx.strokeText(labelText, point.x + 20, point.y - 20);
            ctx.fillText(labelText, point.x + 20, point.y - 20);
        }
    }
}
